import { useState, useEffect } from "react";
import Layout from "../components/Layout";
import { api, toast } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Edit, Loader2, Users, Plus, Trash2, History } from "lucide-react";

export default function HRLeaveAllocations({ user }) {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [allocationData, setAllocationData] = useState({
    casual_total: 8,
    sick_total: 8,
    earned_total: 15,
    maternity_total: 180,
  });
  // EL Encashment state
  const [encashHistory, setEncashHistory] = useState([]);
  const [encashForm, setEncashForm] = useState({ days: "", date: new Date().toISOString().split("T")[0], note: "" });
  const [addingEncash, setAddingEncash] = useState(false);
  const [savingAlloc, setSavingAlloc] = useState(false);
  // Quarterly credit state
  const [quarterlyStatus, setQuarterlyStatus] = useState(null);
  const [runningCredit, setRunningCredit] = useState(false);

  useEffect(() => {
    fetchEmployees();
    fetchQuarterlyStatus();
  }, []);

  const fetchQuarterlyStatus = async () => {
    try {
      const res = await api.get("/leave/quarterly-credit/status");
      setQuarterlyStatus(res.data);
    } catch (e) { /* silently ignore */ }
  };

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const response = await api.get("/employees");
      setEmployees(response.data);
    } catch (error) {
      toast.error("Failed to fetch employees");
    } finally {
      setLoading(false);
    }
  };

  const handleEditAllocation = async (employee) => {
    setSelectedEmployee(employee);
    setEncashForm({ days: "", date: new Date().toISOString().split("T")[0], note: "" });
    try {
      const [balanceRes, historyRes] = await Promise.all([
        api.get(`/leave/balance/${employee.employee_id}`),
        api.get(`/leave/el-encashments/${employee.employee_id}`)
      ]);
      setAllocationData({
        casual_total: balanceRes.data.casual?.remaining ?? balanceRes.data.casual?.total ?? 8,
        sick_total: balanceRes.data.sick?.remaining ?? balanceRes.data.sick?.total ?? 8,
        earned_total: balanceRes.data.earned?.remaining ?? balanceRes.data.earned?.total ?? 15,
        maternity_total: balanceRes.data.maternity?.remaining ?? balanceRes.data.maternity?.total ?? 180,
        // Store full balance for reference
        _earned_annual: balanceRes.data.earned?.total || 15,
      });
      setEncashHistory(historyRes.data || []);
    } catch (error) {
      setAllocationData({ casual_total: 8, sick_total: 8, earned_total: 15, maternity_total: 180 });
      setEncashHistory([]);
    }
    setEditDialogOpen(true);
  };

  const handleSaveAllocation = async (e) => {
    e.preventDefault();
    setSavingAlloc(true);
    try {
      const response = await api.get(`/leave/balance/${selectedEmployee.employee_id}`);
      if (response.data.id) {
        await api.put(`/leave/allocations/${response.data.id}`, allocationData);
        toast.success("Leave allocation updated successfully");
      }
    } catch (error) {
      try {
        await api.post("/leave/allocations", {
          employee_id: selectedEmployee.employee_id,
          year: new Date().getFullYear(),
          ...allocationData
        });
        toast.success("Leave allocation created successfully");
      } catch (createError) {
        toast.error("Failed to save leave allocation");
      }
    } finally {
      setSavingAlloc(false);
      setEditDialogOpen(false);
    }
  };

  const handleAddEncashment = async () => {
    if (!encashForm.days || parseFloat(encashForm.days) <= 0) {
      toast.error("Please enter a valid number of days");
      return;
    }
    if (!encashForm.date) {
      toast.error("Please select a date");
      return;
    }
    setAddingEncash(true);
    try {
      const res = await api.post("/leave/el-encashments", {
        employee_id: selectedEmployee.employee_id,
        days: parseFloat(encashForm.days),
        date: encashForm.date,
        note: encashForm.note || ""
      });
      toast.success("EL encashment recorded");
      setEncashHistory([res.data.encashment, ...encashHistory]);
      setEncashForm({ days: "", date: new Date().toISOString().split("T")[0], note: "" });
      // Refresh the balance display
      const balance = await api.get(`/leave/balance/${selectedEmployee.employee_id}`);
      setAllocationData(prev => ({
        ...prev,
        earned_total: balance.data.earned?.remaining ?? prev.earned_total,
      }));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to record encashment");
    } finally {
      setAddingEncash(false);
    }
  };

  const handleDeleteEncashment = async (encashmentId) => {
    if (!confirm("Delete this encashment record? The EL balance will be restored.")) return;
    try {
      await api.delete(`/leave/el-encashments/${encashmentId}`);
      toast.success("Encashment deleted and balance restored");
      setEncashHistory(encashHistory.filter(e => e.id !== encashmentId));
      // Refresh balance
      const balance = await api.get(`/leave/balance/${selectedEmployee.employee_id}`);
      setAllocationData(prev => ({
        ...prev,
        earned_total: balance.data.earned?.total || prev.earned_total
      }));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete encashment");
    }
  };

  const totalEncashed = encashHistory.reduce((sum, e) => sum + (e.days || 0), 0);

  return (
    <Layout user={user}>
      <div className="p-6 space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-md3-display-small">Leave Allocations</h1>
            <p className="text-md3-on-surface-variant">Manage employee leave balances</p>
          </div>
          <div className="flex flex-col items-end gap-1">
            <Button
              variant="outline"
              disabled={quarterlyStatus?.already_run || runningCredit}
              className={quarterlyStatus?.already_run
                ? "border-gray-200 text-gray-400 cursor-not-allowed"
                : "border-green-300 text-green-700 hover:bg-green-50"}
              onClick={async () => {
                setRunningCredit(true);
                try {
                  const res = await api.post("/leave/quarterly-credit");
                  toast.success(res.data.message + ` — ${res.data.employees_credited} employees credited`);
                  fetchQuarterlyStatus();
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Failed to run quarterly credit");
                } finally {
                  setRunningCredit(false);
                }
              }}
              data-testid="quarterly-credit-btn"
            >
              {runningCredit
                ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Running...</>
                : quarterlyStatus?.already_run
                  ? `Q${quarterlyStatus.quarter} Credit Done`
                  : "Run Quarterly Credit (CL + SL)"
              }
            </Button>
            {quarterlyStatus?.already_run && (
              <span className="text-xs text-gray-400">
                Run by {quarterlyStatus.run_by} · Next active from {quarterlyStatus.next_run_date}
              </span>
            )}
          </div>
        </div>

        <Card className="border-l-4 border-l-blue-500 shadow-md3-1">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-md3-body-small text-md3-on-surface-variant">Total Employees</p>
                <p className="text-md3-display-small">{employees.length}</p>
              </div>
              <Users className="h-12 w-12 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-md3-surface-container shadow-md3-1 rounded-md3-lg border-0">
          <CardHeader>
            <CardTitle className="text-md3-title-large">Employee Leave Allocations</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-gray-400" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee ID</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Department</TableHead>
                    <TableHead>Branch</TableHead>
                    <TableHead>Gender</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {employees.map((emp) => (
                    <TableRow key={emp.id}>
                      <TableCell className="font-medium">{emp.employee_id}</TableCell>
                      <TableCell>{emp.first_name} {emp.last_name}</TableCell>
                      <TableCell><Badge variant="outline">{emp.department}</Badge></TableCell>
                      <TableCell>{emp.branch}</TableCell>
                      <TableCell>{emp.gender || "Not Set"}</TableCell>
                      <TableCell>
                        <Button size="sm" className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary rounded-full"
                          onClick={() => handleEditAllocation(emp)}>
                          <Edit className="h-4 w-4 mr-1" /> Edit Allocation
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Edit Allocation Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small font-semibold">Edit Leave Allocation</DialogTitle>
            </DialogHeader>
            {selectedEmployee && (
              <div className="space-y-6">
                {/* Employee info */}
                <div className="bg-blue-50 p-3 rounded-lg flex items-center justify-between">
                  <div>
                    <p className="font-semibold">{selectedEmployee.first_name} {selectedEmployee.last_name}</p>
                    <p className="text-sm text-gray-500">{selectedEmployee.employee_id} • {selectedEmployee.department}</p>
                  </div>
                </div>

                {/* Leave balance inputs */}
                <form onSubmit={handleSaveAllocation}>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Casual Leave <span className="text-xs text-gray-500">(available days)</span></Label>
                      <Input className="bg-white border-gray-300" type="number" step="0.5" min="0"
                        value={allocationData.casual_total}
                        onChange={(e) => setAllocationData({...allocationData, casual_total: parseFloat(e.target.value) || 0})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Sick Leave <span className="text-xs text-gray-500">(available days)</span></Label>
                      <Input className="bg-white border-gray-300" type="number" step="0.5" min="0"
                        value={allocationData.sick_total}
                        onChange={(e) => setAllocationData({...allocationData, sick_total: parseFloat(e.target.value) || 0})} />
                    </div>
                    <div className="space-y-2">
                      <Label>Earned Leave <span className="text-xs text-gray-500">(available days)</span></Label>
                      <Input className="bg-white border-gray-300" type="number" step="0.5" min="0"
                        value={allocationData.earned_total}
                        onChange={(e) => setAllocationData({...allocationData, earned_total: parseFloat(e.target.value) || 0})} />
                      <p className="text-xs text-gray-500">Cap: 30d (Jr–Dy.Mgr) / 60d (Mgr+)</p>
                    </div>
                    {selectedEmployee.gender === "Female" && (
                      <div className="space-y-2">
                        <Label>Maternity Leave <span className="text-xs text-gray-500">(available days)</span></Label>
                        <Input className="bg-white border-gray-300" type="number" step="0.5" min="0"
                          value={allocationData.maternity_total}
                          onChange={(e) => setAllocationData({...allocationData, maternity_total: parseFloat(e.target.value) || 0})} />
                      </div>
                    )}
                  </div>
                  <div className="flex justify-end mt-4">
                    <Button type="submit" disabled={savingAlloc}
                      className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary rounded-full">
                      {savingAlloc ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</> : "Save Balances"}
                    </Button>
                  </div>
                </form>

                {/* EL Encashment Activity */}
                <div className="border-t pt-5 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <History className="h-5 w-5 text-green-600" />
                      <h3 className="font-semibold text-md3-title-medium">EL Encashment</h3>
                    </div>
                    {totalEncashed > 0 && (
                      <span className="text-sm text-green-700 font-medium bg-green-50 px-3 py-1 rounded-full border border-green-200">
                        Total encashed: {totalEncashed} day{totalEncashed !== 1 ? "s" : ""}
                      </span>
                    )}
                  </div>

                  {/* Add new encashment */}
                  <div className="bg-gray-50 border rounded-lg p-4 space-y-3">
                    <p className="text-sm font-medium text-gray-700">Record New Encashment</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Days *</Label>
                        <Input
                          type="number"
                          step="0.5"
                          min="0.5"
                          placeholder="e.g. 5"
                          className="bg-white border-gray-300 h-9"
                          value={encashForm.days}
                          onChange={(e) => setEncashForm({ ...encashForm, days: e.target.value })}
                          data-testid="encash-days-input"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Date *</Label>
                        <Input
                          type="date"
                          className="bg-white border-gray-300 h-9"
                          value={encashForm.date}
                          onChange={(e) => setEncashForm({ ...encashForm, date: e.target.value })}
                          data-testid="encash-date-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Note (optional)</Label>
                      <Input
                        type="text"
                        placeholder="e.g. Annual encashment"
                        className="bg-white border-gray-300 h-9"
                        value={encashForm.note}
                        onChange={(e) => setEncashForm({ ...encashForm, note: e.target.value })}
                        data-testid="encash-note-input"
                      />
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      className="bg-green-600 hover:bg-green-700 text-white rounded-full"
                      onClick={handleAddEncashment}
                      disabled={addingEncash || !encashForm.days || !encashForm.date}
                      data-testid="add-encashment-btn"
                    >
                      {addingEncash
                        ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" />Recording...</>
                        : <><Plus className="h-3 w-3 mr-1" />Record Encashment</>
                      }
                    </Button>
                  </div>

                  {/* Encashment history */}
                  {encashHistory.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-4">No encashment records yet</p>
                  ) : (
                    <div className="rounded-lg border overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-gray-50">
                            <TableHead className="text-xs">Date</TableHead>
                            <TableHead className="text-xs">Days</TableHead>
                            <TableHead className="text-xs">Note</TableHead>
                            <TableHead className="text-xs">Recorded By</TableHead>
                            <TableHead className="text-xs w-10"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {encashHistory.map((enc) => (
                            <TableRow key={enc.id}>
                              <TableCell className="text-sm font-medium">{enc.date}</TableCell>
                              <TableCell>
                                <span className="text-green-700 font-semibold">{enc.days}d</span>
                              </TableCell>
                              <TableCell className="text-sm text-gray-500">{enc.note || "—"}</TableCell>
                              <TableCell className="text-sm text-gray-500">{enc.recorded_by}</TableCell>
                              <TableCell>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 w-7 p-0 text-red-400 hover:text-red-600 hover:bg-red-50"
                                  onClick={() => handleDeleteEncashment(enc.id)}
                                  data-testid={`delete-encashment-${enc.id}`}
                                  title="Delete & restore balance"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>

                {/* Close */}
                <div className="flex justify-end pt-2 border-t">
                  <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                    Close
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
